/**
 * NetworkMonitorService
 *
 * Monitors browser network connectivity and triggers callbacks on network loss/restoration.
 * Based on legacy PhoneStore.initializeNetworkListener() implementation.
 *
 * Uses browser's online/offline events to detect network changes.
 * This is critical for handling:
 * - Network loss during active calls (play lost connection tone)
 * - Network restoration (attempt automatic reconnection)
 * - Browser sleep/wake cycles
 * - Airplane mode on/off
 */

import { lifecycleLogger as logger } from 'Modules/softphone/utils/logger';

export class NetworkMonitorService {
  private static instance: NetworkMonitorService;
  private initialized = false;
  private onNetworkLossCallback: (() => void) | null = null;
  private onNetworkRestoredCallback: (() => void) | null = null;

  private constructor() {
    logger.info('NetworkMonitorService initialized');
  }

  static getInstance(): NetworkMonitorService {
    if (!NetworkMonitorService.instance) {
      NetworkMonitorService.instance = new NetworkMonitorService();
    }
    return NetworkMonitorService.instance;
  }

  /**
   * Initialize network monitoring
   *
   * @param onNetworkLoss - Callback when network connection is lost
   * @param onNetworkRestored - Callback when network connection is restored
   */
  initialize(
    onNetworkLoss: () => void,
    onNetworkRestored: () => void
  ): void {
    // Skip if already initialized or not in browser environment
    if (this.initialized || typeof window === 'undefined') {
      if (this.initialized) {
        logger.warn('Network monitoring already initialized, skipping');
      }
      return;
    }

    logger.info('Initializing network monitoring');

    this.onNetworkLossCallback = onNetworkLoss;
    this.onNetworkRestoredCallback = onNetworkRestored;

    // Bind event handlers
    window.addEventListener('offline', this.handleOffline);
    window.addEventListener('online', this.handleOnline);

    this.initialized = true;

    // Log initial network state
    logger.debug('Network monitoring active', {
      isOnline: navigator.onLine,
    });
  }

  /**
   * Cleanup network monitoring
   */
  cleanup(): void {
    if (!this.initialized || typeof window === 'undefined') {
      return;
    }

    logger.info('Cleaning up network monitoring');

    window.removeEventListener('offline', this.handleOffline);
    window.removeEventListener('online', this.handleOnline);

    this.onNetworkLossCallback = null;
    this.onNetworkRestoredCallback = null;
    this.initialized = false;
  }

  /**
   * Check if network monitoring is initialized
   */
  getIsInitialized(): boolean {
    return this.initialized;
  }

  /**
   * Handle offline event
   */
  private handleOffline = (): void => {
    logger.warn('Network connection lost (offline event)');

    if (this.onNetworkLossCallback) {
      try {
        this.onNetworkLossCallback();
      } catch (err) {
        logger.error('Error in network loss callback', err as Error);
      }
    }
  };

  /**
   * Handle online event
   */
  private handleOnline = (): void => {
    logger.info('Network connection restored (online event)');

    if (this.onNetworkRestoredCallback) {
      try {
        this.onNetworkRestoredCallback();
      } catch (err) {
        logger.error('Error in network restored callback', err as Error);
      }
    }
  };
}
